import { useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { finalizeApproval } from '../api/data-qa-api';
import { SectionKeyType, SectionStatusType } from '../data-qa-constants';
import { Fingerprint, LoadResult, PriorApproval } from '../data-qa-types';

interface ApprovalSnapshot {
  sectionStatus: Record<SectionKeyType, SectionStatusType>;
  sectionNotes: Record<SectionKeyType, string>;
}

interface ApprovalPatch {
  sectionStatus: Partial<Record<SectionKeyType, SectionStatusType>>;
  sectionNotes: Partial<Record<SectionKeyType, string>>;
}

// finalizeApproval persists correctly, but useMmmDataQaLoad caches each config's
// LoadResult with staleTime: Infinity -- without patching the cache directly,
// switching away from a model and back would show its OLD cached priorApproval,
// silently reverting an approval just made in this same session (confirmed live:
// DTC -> Commerce -> DTC lost a Context Variables approval). finalizeApproval's own
// return value IS the fresh PriorApproval, so patch the cache directly rather than
// paying for a full refetch of this endpoint's own expensive backend computation.
//
// finalizeApproval also overwrites the ENTIRE section snapshot per call, and every
// caller submits a full map, not a single-section patch -- so two calls for the SAME
// configPath racing (e.g. an auto-inherit effect firing while a user's own
// handleApprove call is still in flight) used to let whichever network response
// landed last silently overwrite the other's decision, with no error and no
// indication anything was lost. `approvalSnapshots` tracks each configPath's current
// merged snapshot, updated SYNCHRONOUSLY (before any await) on every call -- so a
// call that starts even microseconds after an earlier one always merges its own
// `patch` on top of that earlier call's effect, regardless of which call's network
// response resolves first. `persistQueues` then also serializes the actual network
// writes per configPath, so the LAST call to be initiated is also the last one to hit
// the backend (both are needed: the map alone fixes payload content, the queue fixes
// write order matching call order).
//
// Pulled into its own hook (rather than living inline in DataQA.tsx) so its refs sit
// outside that component's own render-time closure graph -- DataQA.tsx calls
// renderStep() directly in JSX, and its own handleApprove/handleFlag/etc. (passed down
// through renderStep's returned elements) all close over persistApproval, which the
// react-hooks/refs lint rule then flagged as "a ref reachable from render" even though
// the ref is only ever touched inside this hook's own callback (invoked from click
// handlers and effects, never during render). Isolating the refs in a separate hook
// function keeps that reachability analysis from tracing into DataQA's own render body.
export const usePersistApproval = () => {
  const queryClient = useQueryClient();
  const approvalSnapshots = useRef(new Map<string, ApprovalSnapshot>()).current;
  const persistQueues = useRef(new Map<string, Promise<unknown>>()).current;

  return useCallback(
    (
      targetCid: string,
      configPath: string,
      configHash: string,
      // Used only to seed approvalSnapshots the FIRST time this configPath is written
      // this session -- every subsequent call ignores it and builds on the map's own
      // running snapshot instead (see above).
      baseSnapshot: ApprovalSnapshot,
      patch: ApprovalPatch,
      fingerprints: Fingerprint[]
    ): Promise<PriorApproval> => {
      const seed = approvalSnapshots.get(configPath) ?? baseSnapshot;
      const merged: ApprovalSnapshot = {
        sectionStatus: { ...seed.sectionStatus, ...patch.sectionStatus },
        sectionNotes: { ...seed.sectionNotes, ...patch.sectionNotes }
      };
      approvalSnapshots.set(configPath, merged);

      const previous = persistQueues.get(configPath) ?? Promise.resolve();
      const next = previous
        .catch(() => undefined)
        .then(async () => {
          const priorApproval = await finalizeApproval(
            targetCid,
            configHash,
            merged.sectionStatus,
            merged.sectionNotes,
            fingerprints
          );
          queryClient.setQueryData<LoadResult>(['mmmDataQALoad', targetCid, configPath], old =>
            old ? { ...old, priorApproval } : old
          );
          return priorApproval;
        });
      persistQueues.set(configPath, next);
      return next;
    },
    [queryClient, approvalSnapshots, persistQueues]
  );
};
